import React from 'react';
import { ArrowLeft, Clock, Sparkles, Skull, BookOpen, Sun } from 'lucide-react';

const ComingSoon = ({ theme, onBack }) => {
  const themes = {
    tantra: {
      bg: 'bg-neutral-900',
      text: 'text-red-600',
      accent: 'bg-red-700',
      accentText: 'text-black',
      border: 'border-red-600',
      icon: <Skull size={120} />,
      font: 'font-serif tracking-widest uppercase',
      decorations: ["†", "VOID", "SHADOW", "BLOOD", "†", "RITUAL"],
      title: "THE RITUAL AWAITS",
      subtitle: "Shadow work in progress",
      desc: "The forbidden knowledge is being inscribed. The altar is being prepared.",
      eta: "SUMMONING SOON"
    },
    inkspeak: {
      bg: 'bg-[#f0e6d2]',
      text: 'text-black',
      accent: 'bg-black',
      accentText: 'text-[#f0e6d2]',
      border: 'border-black',
      icon: <BookOpen size={120} />,
      font: 'font-mono',
      decorations: ["\"quote\"", "PAGE_404", "INK", "¶", "DRAFT", "MANUSCRIPT"],
      title: "CHAPTER PENDING",
      subtitle: "First draft loading...",
      desc: "The words are being etched. The manuscript is being bound. The story continues.",
      eta: "PUBLISHING SOON"
    },
    soul: {
      bg: 'bg-indigo-100',
      text: 'text-indigo-900',
      accent: 'bg-orange-300',
      accentText: 'text-indigo-900',
      border: 'border-indigo-900',
      icon: <Sun size={120} />,
      font: 'font-sans tracking-wide',
      decorations: ["777", "AURA", "FLOW", "ॐ", "ALIGN", "RISE"],
      title: "ALIGNMENT IN PROGRESS",
      subtitle: "Journey within loading...",
      desc: "The energies are being balanced. Your path is being illuminated. Prepare to ascend.",
      eta: "AWAKENING SOON"
    }
  };

  const t = themes[theme] || themes.soul;

  return (
    <div className={`min-h-screen ${t.bg} ${t.font} relative overflow-hidden`}>
      {/* Background Decorations */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        {t.decorations.map((item, idx) => (
          <div 
            key={idx}
            className={`absolute font-black text-6xl md:text-8xl opacity-5 select-none ${t.text}`}
            style={{ 
              top: `${10 + (idx * 15) % 80}%`, 
              left: `${5 + (idx * 20) % 90}%`, 
              transform: `rotate(${(idx * 45) - 45}deg)`
            }}
          >
            {item}
          </div>
        ))}
      </div>

      {/* Top Navigation */}
      <nav className={`border-b-4 ${t.border} bg-white/10 backdrop-blur-sm p-4 sticky top-0 z-30`}>
        <div className="flex items-center justify-between max-w-6xl mx-auto">
          <button 
            onClick={onBack}
            className={`flex items-center gap-2 font-black uppercase tracking-wider px-4 py-2 border-2 ${t.border} ${t.accent} ${t.accentText} hover:opacity-80 transition-opacity shadow-[4px_4px_0px_rgba(0,0,0,1)] hover:translate-x-1 hover:translate-y-1 hover:shadow-none`}
          >
            <ArrowLeft size={20} />
            Return to Realms
          </button>
          
          <div className={`hidden md:flex items-center gap-2 font-bold text-sm ${t.text}`}>
            <Clock size={16} className="animate-pulse" />
            <span className="uppercase tracking-widest">{t.eta}</span>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="relative z-10 flex flex-col items-center justify-center min-h-[calc(100vh-80px)] p-6 md:p-12">
        
        {/* Central Icon */}
        <div className={`relative mb-8 ${t.text} opacity-80`}>
          <div className={`absolute inset-0 ${t.accent} blur-3xl opacity-30 scale-150`}></div>
          <div className="relative animate-pulse">
            {t.icon}
          </div>
        </div>

        {/* Title */}
        <h1 className={`text-5xl md:text-8xl lg:text-9xl font-black text-center uppercase leading-[0.8] mb-4 ${t.text}`}>
          {t.title}
        </h1>

        {/* Separator */}
        <div className={`w-32 md:w-48 h-1 ${t.accent} mb-6`}></div>

        {/* Subtitle */}
        <h2 className={`text-xl md:text-3xl font-bold text-center mb-4 ${t.text} opacity-80`}>
          {t.subtitle}
        </h2>

        {/* Description */}
        <p className={`text-center max-w-xl text-base md:text-lg font-medium mb-8 opacity-70 ${t.text}`}>
          {t.desc}
        </p>

        {/* COMING SOON Badge */}
        <div className={`inline-block px-8 py-4 border-4 ${t.border} ${t.accent} ${t.accentText} shadow-[8px_8px_0px_rgba(0,0,0,1)]`}>
          <span className="text-2xl md:text-4xl font-black uppercase tracking-widest">
            COMING SOON
          </span>
        </div>

        {/* Decorative Marquee */}
        <div className={`mt-12 w-full max-w-4xl overflow-hidden border-y-2 ${t.border} py-3`}>
          <div className={`flex whitespace-nowrap animate-marquee ${t.text} font-bold text-sm uppercase tracking-widest`}>
            <span className="mx-8">⟡ {t.eta} ⟡</span>
            <span className="mx-8">⟡ STAY TUNED ⟡</span>
            <span className="mx-8">⟡ {t.eta} ⟡</span>
            <span className="mx-8">⟡ STAY TUNED ⟡</span>
            <span className="mx-8">⟡ {t.eta} ⟡</span>
            <span className="mx-8">⟡ STAY TUNED ⟡</span>
          </div>
        </div>

        {/* Bottom CTA */}
        <button 
          onClick={onBack}
          className={`mt-8 px-6 py-3 border-2 ${t.border} ${t.text} font-bold uppercase tracking-wider hover:${t.accent} hover:${t.accentText} transition-all`}
        >
          Explore Otaku Realm →
        </button>
      </div>

      {/* Corner Decorations */}
      <div className={`absolute top-20 left-4 md:left-8 ${t.text} opacity-20 text-4xl font-black rotate-90`}>
        01
      </div>
      <div className={`absolute bottom-20 right-4 md:right-8 ${t.text} opacity-20 text-4xl font-black -rotate-90`}>
        99
      </div>

      {/* Noise Overlay */}
      <div 
        className="fixed inset-0 pointer-events-none opacity-[0.03] z-50"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
        }}
      />
    </div>
  );
};

export default ComingSoon;
